<template>
  <div>
    <div class="login-header">
      <h2>{{ title }}</h2>
    </div>

    <p v-if="accounts.length === 0" class="empty">{{ t('accountPickerEmpty') }}</p>

    <template v-else>
      <div v-if="filterVisible" class="filter-wrap">
        <input
          v-model="filterText"
          class="filter-input"
          type="text"
          :placeholder="t('accountPickerFilter')"
          :aria-label="t('accountPickerFilter')"
        />
        <button
          v-if="filterText"
          type="button"
          class="filter-clear"
          :aria-label="t('accountPickerFilterClear')"
          :title="t('accountPickerFilterClear')"
          @click="filterText = ''"
        >&times;</button>
      </div>

      <div class="account-list" :aria-label="title">
        <p v-if="visibleAccounts.length === 0" class="empty">
          {{ t('accountPickerFilterEmpty') }}
        </p>
        <label
          v-for="(acc, idx) in visibleAccounts"
          :key="(acc.domainName || '') + '##' + (acc.accountName || '') + ':' + idx"
          class="account-row"
          :class="{ selected: isSelected(acc) }"
        >
          <input
            type="radio"
            :value="acc"
            v-model="selected"
          />
          <img
            v-if="acc.imageUrl"
            class="account-img"
            :src="resolvedImageUrl(acc.imageUrl)"
            alt=""
            @error="onImageError"
          />
          <div v-else class="account-img account-img--placeholder">
            {{ initials(acc) }}
          </div>
          <div class="account-meta">
            <div class="account-display">{{ labelOf(acc) }}</div>
            <div class="account-handle">{{ acc.accountName }}@{{ acc.domainName }}</div>
          </div>
        </label>
      </div>
    </template>

    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <div class="actions">
      <button class="secondary" @click="$emit('cancel')">{{ t('back') }}</button>
      <button :disabled="!selected || busy" @click="confirm">
        {{ t('continue') }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'

const TT_PREFIX = /^\/tt\b/
const TT = '/tt'

/**
 * The user-visible label for an AccessibleAccount: the company name
 * (`displayName`) when present, otherwise the `accountName##domainName`
 * tuple. Used for the row title and as the sort key.
 */
function labelOfAccount(acc) {
  if (!acc) return ''
  const display = (acc.displayName || '').trim()
  if (display) return display
  return (acc.accountName || '') + '##' + (acc.domainName || '')
}

/** The raw `accountName##domainName` handle, regardless of displayName. */
function handleOfAccount(acc) {
  if (!acc) return ''
  return (acc.accountName || '') + '##' + (acc.domainName || '')
}

export default defineComponent({
  name: 'AccountPickerView',

  props: {
    // 'admin' | 'client' — chooses the title/headline only; the
    // account shape is identical for both kinds.
    kind: { type: String, required: true, validator: v => v === 'admin' || v === 'client' },
    // AccessibleAccount[]
    accounts: { type: Array, required: true },
    // Optional pre-selection (e.g. from the lastaccountname cookie).
    preselect: { type: Object, default: null },
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },

  emits: ['pick', 'cancel'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    // Pre-fill the filter input with the raw `accountName##domainName`
    // of the remembered account — but only when the filter is actually
    // shown (>5 entries). For shorter lists the filter would be hidden
    // anyway, so leaving filterText empty avoids surprises if the user
    // later edits the threshold.
    const filterShown = this.accounts.length > 5
    const initialFilter = (filterShown && this.preselect)
      ? handleOfAccount(this.preselect)
      : ''

    return {
      selected: this.preselect || (this.accounts.length === 1 ? this.accounts[0] : null),
      filterText: initialFilter
    }
  },

  computed: {
    title() {
      return this.kind === 'admin'
        ? this.t('accountPickerAdminTitle')
        : this.t('accountPickerClientTitle')
    },
    /** Show the filter input only when the list is non-trivial. */
    filterVisible() {
      return this.accounts.length > 5
    },
    /** Accounts sorted case-insensitively by their effective label
     *  (company name; falls back to accountName##domainName). */
    sortedAccounts() {
      return [...this.accounts].sort((a, b) =>
        labelOfAccount(a).localeCompare(labelOfAccount(b), undefined, { sensitivity: 'base' })
      )
    },
    /** Sorted + filter applied. Empty filter shows everything. Filter
     *  matches case-insensitively against the company-name label AND
     *  the raw `accountName##domainName` handle — so a user who knows
     *  either one finds the row. */
    visibleAccounts() {
      const q = (this.filterText || '').trim().toLowerCase()
      if (!q) return this.sortedAccounts
      return this.sortedAccounts.filter(a => {
        const label  = labelOfAccount(a).toLowerCase()
        const handle = handleOfAccount(a).toLowerCase()
        return label.includes(q) || handle.includes(q)
      })
    }
  },

  methods: {
    labelOf(acc) {
      return labelOfAccount(acc)
    },
    isSelected(acc) {
      return this.selected
          && this.selected.accountName === acc.accountName
          && this.selected.domainName  === acc.domainName
    },
    initials(acc) {
      const name = acc.displayName || acc.accountName || '?'
      return name.slice(0, 2).toUpperCase()
    },
    resolvedImageUrl(url) {
      // Server-side imageUrls are usually absolute paths starting with
      // /api/image/... — make sure they reach the engine under /tt.
      if (!url) return ''
      if (/^https?:/i.test(url)) return url
      if (TT_PREFIX.test(url))   return url
      if (url.startsWith('/api')) return TT + url
      return url
    },
    onImageError(e) {
      // Swap to placeholder by clearing src; the v-if on imageUrl
      // doesn't re-evaluate, so we just hide the broken image.
      try { e.target.style.display = 'none' } catch { /* ignore */ }
    },
    confirm() {
      if (!this.selected || this.busy) return
      this.$emit('pick', this.selected)
    }
  }
})
</script>

<style scoped>
.login-header { margin-bottom: 12px; }
.login-header h2 { margin: 0; font-size: 1.25rem; }

.empty { color: #6b7280; font-size: 14px; padding: 8px 4px; }

.filter-wrap {
  position: relative;
  margin-bottom: 8px;
}
.filter-input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 38px 12px 12px; /* right padding leaves room for the × */
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font: inherit;
  font-size: 16px;
}
.filter-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}
.filter-clear {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  border-radius: 50%;
  padding: 0;
}
.filter-clear:hover { background: #f3f4f6; color: #111827; }

.account-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px;
}
.account-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  background: white;
  transition: border-color .15s, background .15s;
}
.account-row:hover  { border-color: #93c5fd; }
.account-row.selected { border-color: #2563eb; background: #eff6ff; }

.account-row input[type="radio"] { margin: 0; }

.account-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #e5e7eb;
}
.account-img--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
}

.account-meta { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.account-display { font-weight: 500; }
.account-handle  {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-message {
  margin-top: 12px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
}
button {
  padding: 10px 16px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font: inherit;
  cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }
button.secondary { background: #e5e7eb; color: #111827; }
button.secondary:hover { background: #d1d5db; }
</style>
